import type {
    PaginationPageCountConstantType,
    PaginationPageSizeConstantType,
    PaginationRangeConstantType,
    PaginationItemsConstantType,
    PaginationAttributeNode,
    PaginationRangeType,
    PaginationItemType
} from "./type";

export const getIsPaginationAttributeNode = (value: unknown): value is PaginationAttributeNode => {
    return typeof value === "object" && value !== null && "setAttribute" in value && typeof value.setAttribute === "function" && "removeAttribute" in value && typeof value.removeAttribute === "function";
};

const getRange = (start: number, end: number): Array<number> => {
    const length = Math.max(end - start + 1, 0);

    return Array.from({
        length
    }, (_, index) => start + index);
};

export const getPaginationPageCount = ({
    totalItems,
    pageSize
}: PaginationPageCountConstantType): number | undefined => {
    if(totalItems === undefined) {
        return undefined;
    }

    return Math.max(Math.ceil(totalItems / Math.max(pageSize, 1)), 1);
};

export const getPaginationRange = ({
    totalItems,
    pageSize,
    page
}: PaginationRangeConstantType): PaginationRangeType => {
    if(totalItems === 0) {
        return {
            first: 0,
            last: 0
        };
    }

    const first = (page - 1) * pageSize + 1;

    const last = totalItems === undefined ? page * pageSize : Math.min(page * pageSize, totalItems);

    return {
        first,
        last
    };
};

export const getPaginationNextPage = ({
    pageSize,
    newSize,
    page
}: PaginationPageSizeConstantType): number => {
    const firstItem = (page - 1) * pageSize + 1;

    return Math.floor((firstItem - 1) / newSize) + 1;
};

export const getPaginationItems = ({
    boundaryCount,
    siblingCount,
    pageCount,
    page
}: PaginationItemsConstantType): Array<PaginationItemType> => {
    const slotCount = boundaryCount * 2 + siblingCount * 2 + 3;

    if(pageCount <= slotCount) {
        return getRange(1, pageCount).map((item) => ({
            type: "page",
            page: item
        }));
    }

    const startPages = getRange(1, Math.min(boundaryCount, pageCount));

    const endPages = getRange(Math.max(pageCount - boundaryCount + 1, boundaryCount + 1), pageCount);

    const siblingsStart = Math.max(Math.min(page - siblingCount, pageCount - boundaryCount - siblingCount * 2 - 1), boundaryCount + 2);

    const firstEndPage = endPages[0];

    const siblingsEnd = Math.min(Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2), firstEndPage === undefined ? pageCount - 1 : firstEndPage - 2);

    const items: Array<PaginationItemType> = startPages.map((item) => ({
        type: "page",
        page: item
    }));

    if(siblingsStart > boundaryCount + 2) {
        items.push({
            key: "start-ellipsis",
            type: "ellipsis"
        });
    } else if(boundaryCount + 1 < pageCount - boundaryCount) {
        items.push({
            page: boundaryCount + 1,
            type: "page"
        });
    }

    getRange(siblingsStart, siblingsEnd).forEach((item) => {
        items.push({
            type: "page",
            page: item
        });
    });

    if(siblingsEnd < pageCount - boundaryCount - 1) {
        items.push({
            key: "end-ellipsis",
            type: "ellipsis"
        });
    } else if(pageCount - boundaryCount > boundaryCount) {
        items.push({
            page: pageCount - boundaryCount,
            type: "page"
        });
    }

    endPages.forEach((item) => {
        items.push({
            type: "page",
            page: item
        });
    });

    return items;
};
